import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  parteEntradaService, sucursalService, almacenService,
  tiposMovimientoService, entidadService, trabajadorService,
  tablaService, articuloService, unidadMedidaService, tipoCambioService,
  datosGeneralesService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';

const today = () => new Date().toISOString().split('T')[0];
const fmt   = n => isNaN(Number(n)) ? '0.00' : Number(n).toFixed(2);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ESTADO_COLORS = {
  'GENERADO':       { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'APLICADO PAR.':  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'APLICADO TOTAL': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
};

/* Input numérico compacto para la tabla */
const NumInput = ({ value, onChange, placeholder = '0.00', step = '0.01', min = '0' }) => (
  <input
    type="number" value={value} onChange={onChange}
    placeholder={placeholder} step={step} min={min}
    style={{
      width: '100%', height: 28, padding: '0 7px', boxSizing: 'border-box',
      textAlign: 'right', fontSize: 12, fontFamily: 'Inter, sans-serif',
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
      borderRadius: 6, color: '#e2e8f0', outline: 'none',
    }}
    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.09)'; e.target.style.boxShadow = 'none'; }}
  />
);

export default function ParteEntradaValorizado() {
  const navigate = useNavigate();
  const { sucursal: sesionSucursal } = useAuth();

  /* ── Catálogos ─────────────────────────────────────────────────── */
  const [sucursales,      setSucursales]      = useState([]);
  const [almacenes,       setAlmacenes]       = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [entidades,       setEntidades]       = useState([]);
  const [trabajadores,    setTrabajadores]    = useState([]);
  const [tiposDoc,        setTiposDoc]        = useState([]);
  const [centrosCosto,    setCentrosCosto]    = useState([]);
  const [articulos,       setArticulos]       = useState([]);
  const [unidades,        setUnidades]        = useState([]);
  const [loadingCat,      setLoadingCat]      = useState(true);

  /* ── Form ──────────────────────────────────────────────────────── */
  const [form, setForm] = useState({
    id_sucursal: '', id_almacen: '', id_tipo_movimiento: '',
    id_proveedor: '', ruc_proveedor: '', proveedor_nombre: '',
    fecha_operacion: today(),
    moneda: 'PEN', tipo_cambio: '',
    id_tipo_doc_ref1: '', num_doc_ref1: '',
    id_tipo_doc_ref2: '', num_doc_ref2: '',
    observacion: '', id_centro_costo: '', id_solicitante: '',
  });
  const [detalle,    setDetalle]    = useState([]);
  const [nextNumero, setNextNumero] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [creado,     setCreado]     = useState(null);
  const [submitted,  setSubmitted]  = useState(false);

  /* ── Tipo de cambio ─────────────────────────────────────────────── */
  const [loadingTc, setLoadingTc] = useState(false);
  const [tcFuente,  setTcFuente]  = useState(null); // 'local' | 'sunat' | 'no-encontrado'

  const tdSvc = useRef(tablaService('tipos-documento'));
  const ccSvc = useRef(tablaService('centros-costo'));

  /* ── Catálogos ─────────────────────────────────────────────────── */
  const fetchCatalogos = useCallback(async () => {
    setLoadingCat(true);
    try {
      const [suc, alm, tm, ent, trab, td, cc, ums, dg] = await Promise.all([
        sucursalService.getAll(),
        almacenService.getAll(),
        tiposMovimientoService.getAll(),
        entidadService.getAll(),
        trabajadorService.getAll(),
        tdSvc.current.getAll(),
        ccSvc.current.getAll(),
        unidadMedidaService.getAll(),
        datosGeneralesService.get(),
      ]);
      setSucursales(suc.data  || []);
      setAlmacenes(alm.data   || []);
      setTiposMovimiento((tm.data || []).filter(t => t.tipo === 'ENTRADA'));
      const entList = ent.data || [];
      setEntidades(entList);
      setTrabajadores(trab.data || []);
      setTiposDoc(td.data     || []);
      setCentrosCosto(cc.data || []);
      // Artículos: búsqueda remota, no se precargan.
      setArticulos([]);
      setUnidades(ums.data || []);
      // Pre-seleccionar proveedor con la empresa actual
      const rucEmpresa = dg.data?.ruc_empresa;
      if (rucEmpresa) {
        const entEmpresa = entList.find(x => x.ruc === rucEmpresa);
        if (entEmpresa) {
          setForm(f => ({
            ...f,
            id_proveedor:    String(entEmpresa.id_entidad),
            ruc_proveedor:   entEmpresa.ruc || '',
            proveedor_nombre: entEmpresa.razon_social || '',
          }));
        }
      }
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
    if (sesionSucursal?.id_sucursal)
      setForm(f => ({ ...f, id_sucursal: String(sesionSucursal.id_sucursal) }));
  }, [fetchCatalogos, sesionSucursal?.id_sucursal]);

  /* ── Mapas lookup ─────────────────────────────────────────────── */
  const articuloMap = useMemo(
    () => Object.fromEntries(articulos.map(a => [String(a.id_articulo), a])),
    [articulos]
  );
  const unidadMap = useMemo(
    () => Object.fromEntries(unidades.map(u => [String(u.id_unidad), u.codigo])),
    [unidades]
  );

  const tipoMov = useMemo(
    () => tiposMovimiento.find(t => String(t.id_tipo_movimiento) === String(form.id_tipo_movimiento)) || null,
    [tiposMovimiento, form.id_tipo_movimiento]
  );
  const almacenesSucursal = useMemo(
    () => sesionSucursal?.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(sesionSucursal.id_sucursal))
      : almacenes,
    [almacenes, sesionSucursal]
  );

  useEffect(() => {
    if (!form.id_almacen) { setNextNumero(null); return; }
    parteEntradaService.getNextNumero(form.id_almacen)
      .then(r => setNextNumero(r.data.numero))
      .catch(() => setNextNumero(null));
  }, [form.id_almacen]);

  /* ── Tipo de cambio auto-búsqueda ────────────────────────────── */
  const buscarTipoCambio = useCallback(async (fecha, moneda) => {
    if (moneda === 'PEN') {
      setForm(f => ({ ...f, tipo_cambio: '1.000' }));
      setTcFuente(null);
      return;
    }
    if (!fecha) return;
    setLoadingTc(true);
    setTcFuente(null);
    try {
      // 1. Buscar en tabla local
      const res = await tipoCambioService.getByFecha(fecha);
      if (res.data?.tc_venta) {
        setForm(f => ({ ...f, tipo_cambio: String(res.data.tc_venta) }));
        setTcFuente('local');
        return;
      }
    } catch {}
    try {
      // 2. Consultar SUNAT
      const [anio, mes] = fecha.split('-').map(Number);
      const res = await tipoCambioService.consultarSunat(anio, mes);
      const lista = res.data || [];
      const fila  = lista.find(r => r.fecha === fecha);
      if (fila?.venta) {
        setForm(f => ({ ...f, tipo_cambio: String(fila.venta) }));
        setTcFuente('sunat');
        return;
      }
      setTcFuente('no-encontrado');
    } catch {
      setTcFuente('no-encontrado');
    } finally {
      setLoadingTc(false);
    }
  }, []);

  useEffect(() => {
    buscarTipoCambio(form.fecha_operacion, form.moneda);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fecha_operacion, form.moneda]);

  /* ── Helpers ─────────────────────────────────────────────────── */
  const setF    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const req     = (flag, label) => flag ? `${label} *` : label;
  const err     = (val) => submitted && !val ? 'rq-field-required' : '';
  const errIf   = (flag, val) => submitted && flag && !val ? 'rq-field-required' : '';

  const addLinea    = () => setDetalle(p => [...p, {
    _key: Date.now(), id_articulo: '', id_unidad: '', cantidad: 1, costo_unitario: '', igv_pct: 18,
    articulo_codigo: '', articulo_descripcion: '', unidad_descripcion: '',
  }]);
  const removeLinea = k  => setDetalle(p => p.filter(l => l._key !== k));
  const updateLinea = (k, field, val) => setDetalle(p => p.map(l => l._key === k ? { ...l, [field]: val } : l));

  const onSelectArticulo = (key, idArticulo, art) => {
    setDetalle(p => p.map(l =>
      l._key === key
        ? {
            ...l,
            id_articulo: idArticulo || '',
            id_unidad: art?.id_unidad ? String(art.id_unidad) : '',
            articulo_codigo: art?.codigo_interno || '',
            articulo_descripcion: art?.descripcion || '',
            unidad_descripcion: art?.unidad_descripcion || '',
          }
        : l
    ));
  };

  /* ── Cálculos por línea ──────────────────────────────────────── */
  const calcLinea = (l) => {
    const cu  = parseFloat(l.costo_unitario) || 0;
    const pct = parseFloat(l.igv_pct)        || 0;
    const qty = parseFloat(l.cantidad)        || 0;
    const igv_unit = cu * pct / 100;
    const total    = (cu + igv_unit) * qty;
    return { cu, pct, igv_unit, total };
  };

  /* ── Totales ─────────────────────────────────────────────────── */
  const totales = useMemo(() => {
    let gravado = 0, exonerado = 0, igv = 0;
    detalle.forEach(l => {
      const { cu, pct, igv_unit, total: _ } = calcLinea(l);
      const qty = parseFloat(l.cantidad) || 0;
      if (pct > 0) gravado   += cu * qty;
      else          exonerado += cu * qty;
      igv += igv_unit * qty;
    });
    return { gravado, exonerado, igv, total: gravado + exonerado + igv };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle]);

  /* ── Guardar ─────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!form.id_almacen)         { setAlert({ type: 'warning', message: 'Seleccione un almacén.' }); return; }
    if (!form.id_tipo_movimiento) { setAlert({ type: 'warning', message: 'Seleccione un tipo de movimiento.' }); return; }
    if (!form.fecha_operacion)    { setAlert({ type: 'warning', message: 'Ingrese la fecha de operación.' }); return; }
    if (!form.tipo_cambio || parseFloat(form.tipo_cambio) <= 0) {
      setAlert({ type: 'warning', message: 'Ingrese un tipo de cambio válido.' }); return;
    }
    if (tipoMov) {
      if (tipoMov.obliga_ruc          && !form.id_proveedor)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere RUC/Proveedor.' }); return; }
      if (tipoMov.obliga_centro_costo && !form.id_centro_costo)  { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Centro de Costo.' }); return; }
      if (tipoMov.obliga_doc_ref1     && !form.id_tipo_doc_ref1) { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Documento de Referencia 1.' }); return; }
      if (tipoMov.obliga_num_ref1     && !form.num_doc_ref1)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Número de Referencia 1.' }); return; }
      if (tipoMov.obliga_doc_ref2     && !form.id_tipo_doc_ref2) { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Documento de Referencia 2.' }); return; }
      if (tipoMov.obliga_num_ref2     && !form.num_doc_ref2)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Número de Referencia 2.' }); return; }
      if (tipoMov.obliga_glosa        && !form.observacion)      { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Glosa/Observación.' }); return; }
    }
    if (detalle.length === 0) { setAlert({ type: 'warning', message: 'Agregue al menos un artículo.' }); return; }
    const sinArticulo = detalle.find(l => !l.id_articulo);
    if (sinArticulo) {
      const idx = detalle.indexOf(sinArticulo) + 1;
      setAlert({ type: 'warning', message: `El ítem ${idx} no tiene artículo seleccionado.` }); return;
    }
    const cantInvalida = detalle.find(l => !(parseFloat(l.cantidad) > 0));
    if (cantInvalida) {
      const idx = detalle.indexOf(cantInvalida) + 1;
      setAlert({ type: 'warning', message: `El ítem ${idx} tiene cantidad cero o negativa.` }); return;
    }
    const precioInvalido = detalle.find(l => !(parseFloat(l.costo_unitario) > 0));
    if (precioInvalido) {
      const idx = detalle.indexOf(precioInvalido) + 1;
      setAlert({ type: 'warning', message: `El ítem ${idx} no tiene precio unitario válido.` }); return;
    }
    const n = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;
    const payload = {
      id_almacen:         n(form.id_almacen),
      id_sucursal:        n(form.id_sucursal),
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      id_proveedor:       n(form.id_proveedor),
      ruc_proveedor:      form.ruc_proveedor  || null,
      fecha_operacion:    form.fecha_operacion,
      moneda:             form.moneda,
      tipo_cambio:        parseFloat(form.tipo_cambio) || null,
      id_tipo_doc_ref1:   n(form.id_tipo_doc_ref1),
      num_doc_ref1:       form.num_doc_ref1   || null,
      id_tipo_doc_ref2:   n(form.id_tipo_doc_ref2),
      num_doc_ref2:       form.num_doc_ref2   || null,
      observacion:        form.observacion    || null,
      id_centro_costo:    n(form.id_centro_costo),
      id_solicitante:     n(form.id_solicitante),
      detalle: detalle.map((l, i) => {
        const { igv_unit } = calcLinea(l);
        return {
          orden:          i + 1,
          id_articulo:    n(l.id_articulo),
          id_unidad:      n(l.id_unidad),
          cantidad:       parseFloat(l.cantidad)       || 1,
          costo_unitario: parseFloat(l.costo_unitario) || 0,
          igv_pct:        parseFloat(l.igv_pct)        || 0,
        };
      }),
    };

    setSaving(true);
    try {
      const res = await parteEntradaService.create(payload);
      setCreado(res.data);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar el parte de entrada valorizado.') });
    } finally {
      setSaving(false);
    }
  };

  const handleNuevo = () => {
    setCreado(null);
    setSubmitted(false);
    setForm({
      id_sucursal: sesionSucursal?.id_sucursal ? String(sesionSucursal.id_sucursal) : '',
      id_almacen: '', id_tipo_movimiento: '', id_proveedor: '',
      ruc_proveedor: '', proveedor_nombre: '', fecha_operacion: today(),
      moneda: 'PEN', tipo_cambio: '',
      id_tipo_doc_ref1: '', num_doc_ref1: '', id_tipo_doc_ref2: '', num_doc_ref2: '',
      observacion: '', id_centro_costo: '', id_solicitante: '',
    });
    setTcFuente(null);
    setDetalle([]);
    setNextNumero(null);
  };

  /* ── Pantalla de éxito ───────────────────────────────────────── */
  if (creado) {
    const ec = ESTADO_COLORS[creado.estado] || {};
    return (
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>
            PE Valorizado generado
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            PE-{String(creado.numero).padStart(6, '0')}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20 }}>
            {creado.almacen_nombre} · {creado.fecha_operacion?.split('T')[0] || creado.fecha_operacion}
          </div>
          <span style={{ background: ec.bg, color: ec.color, border: `1px solid ${ec.border}`, borderRadius: 12, padding: '4px 16px', fontSize: 11.5, fontWeight: 700 }}>
            {creado.estado}
          </span>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/almacen/parte-entrada/listar')}>Ver listado</button>
            <button className="btn btn-primary" onClick={handleNuevo}>+ Nuevo PE Valorizado</button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ── Formulario ─────────────────────────────────────────────── */
  return (
    <Layout>
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="rq-page">

        {/* ── Header ── */}
        <div className="rq-header">
          <div className="rq-header-left">
            <div className="rq-header-title">
              <div className="rq-header-meta">Almacén</div>
              <div className="rq-header-name">Nuevo PE Valorizado</div>
            </div>
          </div>
          <div className="rq-header-actions">
            {nextNumero && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Correlativo</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#60a5fa', letterSpacing: 1.5 }}>
                  PE-{String(nextNumero).padStart(6, '0')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="rq-body" style={{ overflowY: 'auto' }}>
          <form id="form-pev" onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* SECCIÓN 1: Identificación */}
            <div className="rq-card">
              <div className="rq-card-title">Identificación</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.6fr 0.85fr', gap: '0 14px', alignItems: 'start' }}>
                <AutoComplete
                  label="Sucursal"
                  value={String(form.id_sucursal || '')}
                  options={sucursales} idField="id_sucursal" displayField="nombre"
                  onChange={v => setF('id_sucursal', v)} placeholder="Seleccionar sucursal..."
                />
                <div className={err(form.id_almacen)}>
                  <AutoComplete
                    label="Almacén *"
                    value={String(form.id_almacen || '')}
                    options={almacenesSucursal} idField="id_almacen" displayField="nombre"
                    onChange={v => setF('id_almacen', v)} placeholder="Seleccionar almacén..."
                  />
                </div>
                <div className={err(form.id_tipo_movimiento)}>
                  <AutoComplete
                    label="Tipo de Movimiento *"
                    value={String(form.id_tipo_movimiento || '')}
                    options={tiposMovimiento} idField="id_tipo_movimiento"
                    displayField={t => `[${t.codigo}] ${t.descripcion}`}
                    onChange={v => setF('id_tipo_movimiento', v)} placeholder="Seleccionar tipo..."
                  />
                </div>
                <div className={`form-row ${err(form.fecha_operacion)}`}>
                  <label>Fecha Operación *</label>
                  <input type="date" value={form.fecha_operacion}
                    onChange={e => setF('fecha_operacion', e.target.value)} required />
                </div>
              </div>

              {/* Moneda + Tipo de Cambio */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 200px 1fr', gap: '0 14px', alignItems: 'start', marginTop: 6 }}>
                {/* Moneda */}
                <div className="form-row">
                  <label>Moneda *</label>
                  <select value={form.moneda} onChange={e => setF('moneda', e.target.value)}
                    style={{ width: '100%', appearance: 'none' }}>
                    <option value="PEN">S/ Soles (PEN)</option>
                    <option value="USD">$ Dólares (USD)</option>
                  </select>
                </div>

                {/* Tipo de cambio — siempre visible */}
                <div className={`form-row ${err(form.tipo_cambio && parseFloat(form.tipo_cambio) > 0)}`}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Tipo de Cambio *
                    {loadingTc && (
                      <span style={{ fontSize: 9.5, color: '#60a5fa', fontWeight: 600, letterSpacing: '0.4px' }}>
                        buscando...
                      </span>
                    )}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number" step="0.001" min="0" value={form.tipo_cambio}
                      onChange={e => { setF('tipo_cambio', e.target.value); setTcFuente(null); }}
                      placeholder="0.000"
                      readOnly={form.moneda === 'PEN'}
                      style={{ paddingRight: tcFuente && tcFuente !== 'no-encontrado' ? 60 : undefined, opacity: form.moneda === 'PEN' ? 0.6 : 1 }}
                    />
                    {tcFuente && tcFuente !== 'no-encontrado' && (
                      <span style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
                        color: tcFuente === 'local' ? '#22c55e' : '#60a5fa',
                        background: tcFuente === 'local' ? 'rgba(34,197,94,0.12)' : 'rgba(96,165,250,0.12)',
                        border: `1px solid ${tcFuente === 'local' ? 'rgba(34,197,94,0.3)' : 'rgba(96,165,250,0.3)'}`,
                        borderRadius: 4, padding: '1px 5px', pointerEvents: 'none',
                      }}>
                        {tcFuente === 'local' ? 'LOCAL' : 'SUNAT'}
                      </span>
                    )}
                  </div>
                  {tcFuente === 'no-encontrado' && (
                    <div style={{ fontSize: 10, color: '#fb923c', marginTop: 3 }}>
                      No se encontró TC para esta fecha — ingrese manualmente
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SECCIÓN 2: Proveedor + Referencias */}
            <div className="rq-card">
              <div className="rq-card-title">Proveedor / Referencias</div>
              <div className="rq-prov-obs-grid" style={{ gridTemplateColumns: '1.8fr 1fr 1.3fr 1fr 1.3fr' }}>
                <div className={errIf(tipoMov?.obliga_ruc, form.id_proveedor)}>
                  <AutoComplete
                    label={req(tipoMov?.obliga_ruc, 'RUC / Proveedor')}
                    value={String(form.id_proveedor || '')}
                    options={entidades} idField="id_entidad"
                    displayField={e => `${e.ruc || ''} — ${e.razon_social}`}
                    onChange={v => {
                      const ent = entidades.find(x => String(x.id_entidad) === String(v));
                      setForm(f => ({ ...f, id_proveedor: v || '', ruc_proveedor: ent?.ruc || '', proveedor_nombre: ent?.razon_social || '' }));
                    }}
                    placeholder="Buscar por RUC o nombre..."
                  />
                </div>
                <div className={errIf(tipoMov?.obliga_doc_ref1, form.id_tipo_doc_ref1)}>
                  <AutoComplete
                    label={req(tipoMov?.obliga_doc_ref1, 'Doc. Ref. 1')}
                    value={String(form.id_tipo_doc_ref1 || '')}
                    options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                    onChange={v => setF('id_tipo_doc_ref1', v)} placeholder="Tipo documento..."
                  />
                </div>
                <div className={`form-row ${errIf(tipoMov?.obliga_num_ref1, form.num_doc_ref1)}`}>
                  <label>{req(tipoMov?.obliga_num_ref1, 'Nº Ref. 1')}</label>
                  <input value={form.num_doc_ref1} onChange={e => setF('num_doc_ref1', e.target.value)} placeholder="Número referencia..." />
                </div>
                <div className={errIf(tipoMov?.obliga_doc_ref2, form.id_tipo_doc_ref2)}>
                  <AutoComplete
                    label={req(tipoMov?.obliga_doc_ref2, 'Doc. Ref. 2')}
                    value={String(form.id_tipo_doc_ref2 || '')}
                    options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                    onChange={v => setF('id_tipo_doc_ref2', v)} placeholder="Tipo documento..."
                  />
                </div>
                <div className={`form-row ${errIf(tipoMov?.obliga_num_ref2, form.num_doc_ref2)}`}>
                  <label>{req(tipoMov?.obliga_num_ref2, 'Nº Ref. 2')}</label>
                  <input value={form.num_doc_ref2} onChange={e => setF('num_doc_ref2', e.target.value)} placeholder="Número referencia..." />
                </div>
              </div>
            </div>

            {/* SECCIÓN 3: Datos Adicionales */}
            <div className="rq-card">
              <div className="rq-card-title">Datos Adicionales</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'stretch' }}>
                <div className={errIf(tipoMov?.obliga_centro_costo, form.id_centro_costo)}>
                  <AutoComplete
                    label={req(tipoMov?.obliga_centro_costo, 'Centro de Costo')}
                    value={String(form.id_centro_costo || '')}
                    options={centrosCosto} idField="id_centro_costo" displayField="descripcion"
                    onChange={v => setF('id_centro_costo', v)} placeholder="Centro de costo..."
                    twoLine
                  />
                </div>
                <AutoComplete
                  label="Solicitante"
                  value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setF('id_solicitante', v)} placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                />
                <div className={`form-row ${errIf(tipoMov?.obliga_glosa, form.observacion)}`}
                  style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                  <label>{req(tipoMov?.obliga_glosa, 'Observaciones / Glosa')}</label>
                  <textarea value={form.observacion} onChange={e => setF('observacion', e.target.value)}
                    placeholder="Observaciones del parte de entrada..."
                    style={{
                      flex: 1, width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                      fontSize: 12.5, borderRadius: 8, resize: 'none', minHeight: 44,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)',
                      color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            </div>

            {/* SECCIÓN 4: Artículos con costos */}
            <div className="rq-card" style={{ flex: 'none' }}>
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos</span>
                  <button type="button" className="rq-btn-add-line" onClick={addLinea}>
                    + Agregar ítem
                  </button>
                </div>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {detalle.length} ítem{detalle.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Tabla con scroll horizontal */}
              <div style={{
                border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9,
                overflowX: 'auto', background: 'rgba(0,0,0,0.12)',
                minHeight: 120,
              }}>
                <table className="rq-articles" style={{ minWidth: 860 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th style={{ width: 120 }}>Código</th>
                      <th>Descripción</th>
                      <th style={{ width: 60, textAlign: 'center' }}>U.M.</th>
                      <th style={{ width: 78, textAlign: 'right' }}>Cantidad</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Costo Unit.</th>
                      <th style={{ width: 62, textAlign: 'center' }}>% IGV</th>
                      <th style={{ width: 90, textAlign: 'right' }}>IGV Unit.</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Total</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="rq-empty">
                          Sin artículos — use "+ Agregar ítem" para comenzar
                        </td>
                      </tr>
                    ) : detalle.map((linea, idx) => {
                      const { igv_unit, total } = calcLinea(linea);
                      return (
                        <tr key={linea._key}>
                          <td className="rq-td-num">{idx + 1}</td>
                          <td className={`rq-td-cod ${submitted && !linea.id_articulo ? 'rq-field-required' : ''}`}>
                            <AutoComplete
                              label="" value={String(linea.id_articulo || '')}
                              idField="id_articulo"
                              displayField={(a) => `[${a.codigo_interno || ''}] ${a.descripcion || ''}`}
                              selectedField="codigo_interno"
                              dropdownMinWidth={400}
                              asyncSearch={async (q) => { const r = await articuloService.search(q); return r.data || []; }}
                              minChars={3}
                              initialSelected={linea.id_articulo ? {
                                id_articulo: linea.id_articulo,
                                codigo_interno: linea.articulo_codigo,
                                descripcion: linea.articulo_descripcion,
                                id_unidad: linea.id_unidad,
                                unidad_descripcion: linea.unidad_descripcion,
                              } : null}
                              onChange={(v, item) => onSelectArticulo(linea._key, v, item)}
                              placeholder="Buscar (mín. 3)..."
                            />
                          </td>
                          <td className="rq-td-desc">
                            <span className="rq-td-text">{linea.articulo_descripcion || ''}</span>
                          </td>
                          <td className="rq-td-um">
                            <span className="rq-td-text">{linea.unidad_descripcion || ''}</span>
                          </td>
                          <td className={`rq-td-qty ${submitted && !(parseFloat(linea.cantidad) > 0) ? 'rq-field-required' : ''}`}>
                            <NumInput value={linea.cantidad} step="0.0001" min="0.0001"
                              onChange={e => updateLinea(linea._key, 'cantidad', e.target.value)} />
                          </td>
                          {/* Costo Unitario */}
                          <td className={submitted && !(parseFloat(linea.costo_unitario) > 0) ? 'rq-field-required' : ''}
                            style={{ width: 100, padding: '3px 6px', verticalAlign: 'middle' }}>
                            <NumInput value={linea.costo_unitario} step="0.0001"
                              onChange={e => updateLinea(linea._key, 'costo_unitario', e.target.value)} />
                          </td>
                          {/* % IGV */}
                          <td style={{ width: 62, padding: '3px 6px', verticalAlign: 'middle' }}>
                            <NumInput value={linea.igv_pct} step="0.01" max="100"
                              onChange={e => updateLinea(linea._key, 'igv_pct', e.target.value)} />
                          </td>
                          {/* IGV Unitario (calculado) */}
                          <td style={{ textAlign: 'right', padding: '3px 10px 3px 6px', verticalAlign: 'middle' }}>
                            <span style={{ fontSize: 12, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(igv_unit)}
                            </span>
                          </td>
                          {/* Total línea (calculado) */}
                          <td style={{ textAlign: 'right', padding: '3px 10px 3px 6px', verticalAlign: 'middle' }}>
                            <span style={{ fontSize: 12.5, color: '#e2e8f0', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(total)}
                            </span>
                          </td>
                          <td className="rq-td-del">
                            <button type="button" className="rq-btn-del"
                              onClick={() => removeLinea(linea._key)} title="Eliminar línea">
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Resumen de Totales ── */}
            {detalle.length > 0 && (
              <div className="oc-totales" style={{ marginBottom: 4 }}>
                <div className="oc-totales-card">
                  {totales.gravado > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label">Base Imponible</span>
                      <span className="oc-total-value">{fmt(totales.gravado)}</span>
                    </div>
                  )}
                  {totales.exonerado > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label">Exonerado</span>
                      <span className="oc-total-value">{fmt(totales.exonerado)}</span>
                    </div>
                  )}
                  {totales.igv > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label" style={{ color: '#60a5fa' }}>IGV</span>
                      <span className="oc-total-value" style={{ color: '#60a5fa' }}>{fmt(totales.igv)}</span>
                    </div>
                  )}
                  <div className="oc-total-row main">
                    <span className="oc-total-label">Total</span>
                    <span className="oc-total-value">{fmt(totales.total)}</span>
                  </div>
                </div>
              </div>
            )}

          </form>
        </div>

        {/* ── Footer ── */}
        <div style={{
          flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)',
          padding: '10px 24px', display: 'flex', justifyContent: 'flex-end',
          gap: 10, background: 'rgba(0,0,0,0.18)',
        }}>
          <button type="submit" form="form-pev" className="btn btn-primary"
            disabled={saving || loadingCat} style={{ minWidth: 160 }}>
            {saving ? 'Guardando...' : 'Guardar PE Valorizado'}
          </button>
        </div>

      </div>
    </Layout>
  );
}
